<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits([
    "update:visible",
    "add-language"
]);

const { t } = useI18n();

// Form data
const newLanguageCode = ref("");
const newLanguageName = ref("");

// Watch for dialog visibility changes to reset form
watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        // Reset form when dialog opens
        newLanguageCode.value = "";
        newLanguageName.value = "";
    }
});

const handleClose = () => {
    emit("update:visible", false);
};

const handleCancel = () => {
    newLanguageCode.value = "";
    newLanguageName.value = "";
    handleClose();
};

const handleConfirm = () => {
    if (!newLanguageCode.value.trim()) return;
    
    const languageCode = newLanguageCode.value.toLowerCase().trim();
    const languageName = newLanguageName.value.trim() || languageCode.toUpperCase();
    
    emit("add-language", { code: languageCode, name: languageName });
    
    // Reset form and close
    newLanguageCode.value = "";
    newLanguageName.value = "";
    handleClose();
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="handleClose"
        modal
        :header="t('textMaintenance.addLanguageDialog.title')"
        :style="{ width: '400px' }"
        data-cy="text-maintenance_add-language-dialog"
    >
        <div class="flex flex-col gap-4">
            <div>
                <label for="languageCode" class="block text-sm font-medium mb-1">
                    {{ t('textMaintenance.addLanguageDialog.languageCode') }}
                </label>
                <InputText
                    id="languageCode"
                    v-model="newLanguageCode"
                    :placeholder="t('textMaintenance.addLanguageDialog.languageCodePlaceholder')"
                    class="w-full"
                    maxlength="10"
                    data-cy="text-maintenance_new-language-code-input"
                />
            </div>
            
            <div>
                <label for="languageName" class="block text-sm font-medium mb-1">
                    {{ t('textMaintenance.addLanguageDialog.languageName') }}
                </label>
                <InputText
                    id="languageName"
                    v-model="newLanguageName"
                    :placeholder="t('textMaintenance.addLanguageDialog.languageNamePlaceholder')"
                    class="w-full"
                    data-cy="text-maintenance_new-language-name-input"
                />
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="t('buttons.cancel')"
                    severity="secondary"
                    @click="handleCancel"
                    data-cy="text-maintenance_add-language-cancel-button"
                />
                <Button
                    :label="t('buttons.add')"
                    :disabled="!newLanguageCode.trim()"
                    @click="handleConfirm"
                    data-cy="text-maintenance_add-language-confirm-button"
                />
            </div>
        </template>
    </Dialog>
</template>